import { zGuard } from "../../zod/utils.js";
import { z } from "zod";

import {
  AgentChatRunId,
  TaoProjectId,
  TaoProjectRefId,
} from "../../idTypeBrands.js";
import { getNormalEnum } from "../../pseudoEnums.js";

export function parseAgentLocationType(value: unknown): AgentLocationType {
  return AgentLocationTypeLiteral.parse(
    value === "EDIT_SESSION_BRANCH" ? "TAO_PROJECT_REF" : value,
  );
}

export const AgentChatThreadTypeLiteral = z.enum([
  // MagicEvent-based threads
  // AgentChatMessage-based threads
  "LOGIC_VIEW_V2",
  "ASK",
  "REVIEW",
  "ARTIFACT_EXTRACTION",
  "HERO_ANALYZER",
  "HERO_AGGREGATOR",
  "KNOWLEDGE_CURATION",
  "SEMANTIC_AUTHORING",
  // Unused/dormant
  "LOGIC_VIEW",
  "CHAT_WITH_APP",
  "EXPLORE",
  "CODE_PROJECT",
  // External sessions of MCP tool calls from a non-Hex agent
  "EXTERNAL_SESSION",
]);
export type AgentChatThreadType = z.infer<typeof AgentChatThreadTypeLiteral>;
export const AgentChatThreadType = getNormalEnum(AgentChatThreadTypeLiteral);

// TODO: add AgentChatThreadType.ANALYTICS when that gets added
export const ANALYTICS_AGENT_TYPES: AgentChatThreadType[] = [
  AgentChatThreadType.LOGIC_VIEW_V2,
  AgentChatThreadType.ASK,
];

export const AgentChatThreadAutoSaveHexVersionStatusLiteral = z.enum([
  "PENDING",
  "SUCCEEDED",
  "FAILED",
]);
export type AgentChatThreadAutoSaveHexVersionStatus = z.infer<
  typeof AgentChatThreadAutoSaveHexVersionStatusLiteral
>;
export const AgentChatThreadAutoSaveHexVersionStatus = getNormalEnum(
  AgentChatThreadAutoSaveHexVersionStatusLiteral,
);

/**
 * A classification of a thread, based on what type of work the agent is actually doing.
 */
export const AgentChatThreadClassificationLiteral = z.enum([
  "ANALYSIS",
  "APP_BUILDING",
  "QUICK_QUESTION",
  "DEEPER_ANALYSIS",
]);
export type AgentChatThreadClassification = z.infer<
  typeof AgentChatThreadClassificationLiteral
>;
export const AgentChatThreadClassification = getNormalEnum(
  AgentChatThreadClassificationLiteral,
);

/**
 * The collapsed classifications that reads/UI collapse to. `QUICK_QUESTION` and
 * `DEEPER_ANALYSIS` both normalize into `ANALYSIS`.
 */
export const CollapsedAgentChatThreadClassificationLiteral = z.enum([
  "ANALYSIS",
  "APP_BUILDING",
]);
export type CollapsedAgentChatThreadClassification = z.infer<
  typeof CollapsedAgentChatThreadClassificationLiteral
>;
export const CollapsedAgentChatThreadClassification = getNormalEnum(
  CollapsedAgentChatThreadClassificationLiteral,
);

const LEGACY_ANALYSIS_CLASSIFICATIONS: ReadonlySet<string> = new Set([
  "QUICK_QUESTION",
  "DEEPER_ANALYSIS",
]);

/**
 * Collapses old Quick Question/Deeper Analysis classifications into just Analysis.
 */
export function normalizeAgentChatThreadClassification(
  classification: string | null | undefined,
): CollapsedAgentChatThreadClassification | null {
  if (classification == null) {
    return null;
  }
  if (LEGACY_ANALYSIS_CLASSIFICATIONS.has(classification)) {
    return CollapsedAgentChatThreadClassification.ANALYSIS;
  }
  if (zGuard(CollapsedAgentChatThreadClassificationLiteral, classification)) {
    return classification;
  }
  return null;
}

/**
 * Escalation order for the collapsed classifications: a thread can go
 * from ANALYSIS -> APP_BUILDING but not vice versa.
 */
export const AGENT_CHAT_THREAD_CLASSIFICATION_ORDER: readonly CollapsedAgentChatThreadClassification[] =
  [
    CollapsedAgentChatThreadClassification.ANALYSIS,
    CollapsedAgentChatThreadClassification.APP_BUILDING,
  ];

export function getAgentChatThreadClassificationRank(
  classification: AgentChatThreadClassification,
): number {
  const normalized = normalizeAgentChatThreadClassification(classification);
  return normalized == null
    ? -1
    : AGENT_CHAT_THREAD_CLASSIFICATION_ORDER.indexOf(normalized);
}

/**
 * Get the classification of a thread, given its current classification and the latest one
 * from the artifact extraction agent.
 *
 * Ranking is done on the canonical (collapsed) values, but the original value is
 * returned so that writes preserve whatever shape the caller produced (legacy
 * values during the migration window, `ANALYSIS` afterwards).
 */
export function mergeAgentChatThreadClassification(
  current: AgentChatThreadClassification | null,
  next: AgentChatThreadClassification,
): AgentChatThreadClassification {
  if (current == null) {
    return next;
  }
  return getAgentChatThreadClassificationRank(next) >
    getAgentChatThreadClassificationRank(current)
    ? next
    : current;
}

/**
 * Deterministic thread classification used as a fallback
 *   - LOGIC_VIEW_V2 (notebook) threads    -> APP_BUILDING
 *   - ASK                                 -> ANALYSIS
 */
export function getDeterministicThreadClassification({
  threadType,
}: {
  threadType: AgentChatThreadType;
}): AgentChatThreadClassification | null {
  switch (threadType) {
    case AgentChatThreadType.LOGIC_VIEW_V2:
      return AgentChatThreadClassification.APP_BUILDING;
    case AgentChatThreadType.ASK:
      return AgentChatThreadClassification.ANALYSIS;
    default:
      return null;
  }
}

/**
 * User-facing display names for thread classifications for Context Studio surfaces.
 * Legacy values are shown under the collapsed `Analysis` label.
 */
export const AGENT_CHAT_THREAD_CLASSIFICATION_DISPLAY_NAME: Record<
  AgentChatThreadClassification,
  string
> = {
  [AgentChatThreadClassification.ANALYSIS]: "Analysis",
  [AgentChatThreadClassification.APP_BUILDING]: "App building",
  [AgentChatThreadClassification.QUICK_QUESTION]: "Analysis",
  [AgentChatThreadClassification.DEEPER_ANALYSIS]: "Analysis",
};

export const getAgentChatThreadClassificationDisplayName = (
  classification: AgentChatThreadClassification,
): string => {
  const normalized = normalizeAgentChatThreadClassification(classification);
  return normalized == null
    ? "Unknown"
    : AGENT_CHAT_THREAD_CLASSIFICATION_DISPLAY_NAME[normalized];
};

/**
 * Reason why a thread is returned as an `UnknownAgentChatThread`:
 *  - `SENSITIVE`: the thread is private, and contents are hidden from everyone except the user who created it.
 *  - `ACCESS_DOWNGRADED`: the user does not have permission to view this thread
 *    (e.g. they lack access to a data connection used within the thread, secret, etc.).
 */
export const AgentChatThreadRestrictedReasonLiteral = z.enum([
  "SENSITIVE",
  "ACCESS_DOWNGRADED",
]);
export type AgentChatThreadRestrictedReason = z.infer<
  typeof AgentChatThreadRestrictedReasonLiteral
>;
export const AgentChatThreadRestrictedReason = getNormalEnum(
  AgentChatThreadRestrictedReasonLiteral,
);

/**
 * User-facing display names for each `AgentChatThreadType`.
 */
export const AGENT_CHAT_THREAD_TYPE_DISPLAY_NAME: Record<
  AgentChatThreadType,
  string
> = {
  [AgentChatThreadType.ASK]: "Threads",
  [AgentChatThreadType.LOGIC_VIEW]: "Notebook",
  [AgentChatThreadType.LOGIC_VIEW_V2]: "Notebook",
  [AgentChatThreadType.REVIEW]: "Review",
  [AgentChatThreadType.ARTIFACT_EXTRACTION]: "Artifact Extraction",
  [AgentChatThreadType.HERO_ANALYZER]: "Hero Analyzer",
  [AgentChatThreadType.HERO_AGGREGATOR]: "Hero Aggregator",
  [AgentChatThreadType.KNOWLEDGE_CURATION]: "Knowledge Curation",
  [AgentChatThreadType.SEMANTIC_AUTHORING]: "Modeling",
  [AgentChatThreadType.EXPLORE]: "Explore",
  [AgentChatThreadType.CHAT_WITH_APP]: "Published app",
  [AgentChatThreadType.CODE_PROJECT]: "Code project agent",
  [AgentChatThreadType.EXTERNAL_SESSION]: "External agent",
};

/**
 * Types that only appear as subagent identifiers, never as AgentChatThread.type in the DB.
 */
export const AgentSubagentTypeLiteral = z.enum([
  "DATA_DISCOVERY",
  "VISUALIZATION",
  "DATA_APP",
]);
export type AgentSubagentType = z.infer<typeof AgentSubagentTypeLiteral>;
export const AgentSubagentType = getNormalEnum(AgentSubagentTypeLiteral);

/**
 * Combined type covering both user-facing thread types and subagent-only types.
 */
export type AgentChatThreadOrSubagentType =
  | AgentChatThreadType
  | AgentSubagentType;

export const AgentLocationTypeLiteral = z.enum([
  "HOMEPAGE",
  "TAO_PROJECT_REF",
  "EXTERNAL",
  "CONTEXT_PREVIEW",
]);
export type AgentLocationType = z.infer<typeof AgentLocationTypeLiteral>;
export const AgentLocationType = getNormalEnum(AgentLocationTypeLiteral);

export const TaoEntryTileLiteral = z.enum([
  "DASHBOARD",
  "DATA_STORY",
  "DATA_EXPLORER",
  "WHAT_IF",
  "SLIDES",
  "OTHER_APP",
]);
export type TaoEntryTile = z.infer<typeof TaoEntryTileLiteral>;
export const TaoEntryTile = getNormalEnum(TaoEntryTileLiteral);

/**
 * Caller surface and project context for a message handled by Hex's Code Project agent.
 * EXTERNAL means that agent was invoked outside the Hex UI, not that a different
 * agent or execution environment is running the work.
 *
 * - HOMEPAGE: sent from the Hex homepage; may include a thread-backing branch
 * - TAO_PROJECT_REF: sent from the edit view of a plain-code project;
 *   the project is derived from taoProjectRefId via tao_project_ref.projectId
 * - EXTERNAL: invoked through MCP / CLI / API; may include the thread's backing
 *   branch
 * - CONTEXT_PREVIEW: sent from the context workbench to test unpublished
 *   guides; no location reference
 */
export type AgentLocation =
  | {
      type: typeof AgentLocationType.HOMEPAGE;
      taoProjectRefId?: TaoProjectRefId | null;
      projectTitle?: string | null;
    }
  | {
      type: typeof AgentLocationType.TAO_PROJECT_REF;
      taoProjectRefId: TaoProjectRefId;
      projectTitle?: string | null;
      branchName?: string | null;
    }
  | {
      type: typeof AgentLocationType.EXTERNAL;
      taoProjectRefId?: TaoProjectRefId | null;
      projectTitle?: string | null;
    }
  | {
      type: typeof AgentLocationType.CONTEXT_PREVIEW;
      projectId?: TaoProjectId | null;
      projectTitle?: string | null;
    };

export const INTERNAL_BACKGROUND_AGENT_CHAT_THREAD_TYPES = [
  AgentChatThreadType.REVIEW,
  AgentChatThreadType.ARTIFACT_EXTRACTION,
  AgentChatThreadType.KNOWLEDGE_CURATION,
  AgentChatThreadType.HERO_ANALYZER,
  AgentChatThreadType.HERO_AGGREGATOR,
] as const satisfies readonly AgentChatThreadType[];

export const CONTEXT_STUDIO_HIDDEN_AGENT_CHAT_THREAD_TYPES = [
  ...INTERNAL_BACKGROUND_AGENT_CHAT_THREAD_TYPES,
  AgentChatThreadType.EXTERNAL_SESSION,
] as const satisfies readonly AgentChatThreadType[];

export type LogicViewChatState = "closed" | "chat" | "history";
export const AgentEntryPointLiteral = z.enum([
  "FIX_CELL_WITH_AGENT",
  "SLACK",
  "MCP",
  "AGENT_SUGGESTION",
  "CONTEXT_WORKBENCH_PREVIEW",
  "SEMANTIC_PROJECT_WORKBENCH_PREVIEW",
  "PUBLISHED_APP",
  "PUBLIC_API",
  "TASK",
  "CUSTOMER_EVAL",
  "HOMEPAGE",
  "PROJECT_THREAD_FULLSCREEN",
  "PROJECT_THREAD_NOTEBOOK_SPLIT",
]);
export type AgentEntryPoint = z.infer<typeof AgentEntryPointLiteral>;
export const AgentEntryPoint = getNormalEnum(AgentEntryPointLiteral);

export type AppAgentChatState = "closed" | "chat" | "history";

/**
 * Context for subagent message isolation.
 * Contains both the subagent type and a unique invocation ID.
 */
export interface SubagentContext {
  subagentType: AgentSubagentType;
  subagentInvocationId: AgentChatRunId;
}

/**
 * Status for queued prompts in an agent chat thread.
 */
export const QueuedPromptStatusLiteral = z.enum([
  /** Prompt is waiting to be processed */
  "PENDING",
  /** Prompt is marked to run next, ahead of the normal queue */
  "FORCED",
]);
export type QueuedPromptStatus = z.infer<typeof QueuedPromptStatusLiteral>;
export const QueuedPromptStatus = getNormalEnum(QueuedPromptStatusLiteral);

/**
 * Upper bound on the number of attachments (mentioned tables, datasets, views,
 * projects, cells, and data connections) that can be sent when creating a
 * thread. Defends against unnecessary prompt bloat since attachments are
 * inlined into the prompt.
 */
export const MAX_PROMPT_ATTACHMENTS = 150;

/**
 * Source of a thread as exposed via the public/external APIs.
 *
 * TODO(@acorn): consider merging this with {@link AgentChatMessageSource}. As
 * of this writing, it's unclear if these should be 1:1 and we want to be
 * intentional about what we expose via the API.
 */
export const ApiThreadsSourceSchema = z.enum([
  "HEX",
  "SLACK",
  "MCP",
  "PUBLIC_API",
]);
export type ApiThreadsSource = z.infer<typeof ApiThreadsSourceSchema>;

export const getAgentChatThreadTypeDisplayName = (
  type: AgentChatThreadType,
): string => {
  return AGENT_CHAT_THREAD_TYPE_DISPLAY_NAME[type] ?? "Unknown";
};
